import type {
    CreateExtArgs,
    CreateExtensionArgument,
    Extension,
    ExtensionAPI,
    Null,
    SnapshotDal
} from '@wix/document-manager-core'
import {Action, AtomicTransaction, InitiatorType, PendingTransaction, SaveRequest} from './serverProtocolTypes'
import type {SafeRemovalError} from '../schema/schema'
import type {DocumentServicesModelExtApi} from '../documentServicesModel'
import * as converter from './csaveConverter'
import _ from 'lodash'

export interface CreateTransactionReq {
    payload?: SaveRequest
    error?: SafeRemovalError
}

const createExtension = ({experimentInstance, logger}: CreateExtensionArgument): Extension => {
    const createExtensionAPI = ({extensionAPI}: CreateExtArgs): ContinuousSavePayloadBuilderExtensionAPI => {
        const createFragmentTransactions = (
            fromSnapshot: Null<SnapshotDal>,
            toSnapshot: SnapshotDal,
            lastTransactionId?: string
        ): CreateTransactionReq => {
            const {siteAPI} = extensionAPI as DocumentServicesModelExtApi
            const siteVersion = `${siteAPI.getSiteVersion()}`
            const {atomicResult, error} = converter.convertFragmentsData(
                fromSnapshot,
                toSnapshot,
                logger,
                experimentInstance,
                extensionAPI
            )
            if (error) {
                return {error}
            }
            if (!atomicResult) {
                throw new Error('No atomicResult')
            }
            if (_.every(_.values(atomicResult), _.isEmpty)) {
                return {
                    payload: {
                        atomicTransactions: []
                    }
                }
            }
            const fragmentTransactions: PendingTransaction[] = _.map(atomicResult, diff => ({
                correlationId: toSnapshot.id,
                actions: diff! as Action[],
                metadata: {
                    correlationId: toSnapshot.id,
                    lastTransactionId,
                    siteVersion,
                    initiator: {initiatorType: InitiatorType.DM_CLIENT}
                },
                envSessionId: siteAPI.getEnvSessionId()
            }))
            const payload: SaveRequest = experimentInstance.isOpen('dm_moveCsaveToNewProto')
                ? {
                      atomicTransactions:
                          fragmentTransactions.length > 1
                              ? [{transactionGroup: {transactions: fragmentTransactions}}]
                              : ([{transaction: fragmentTransactions[0]}] as AtomicTransaction[]),
                      branchId: siteAPI.getBranchId()
                  }
                : {transactions: fragmentTransactions, branchId: siteAPI.getBranchId()}
            return {payload}
        }

        const createTransactionsCSave = (
            fromSnapshot: Null<SnapshotDal>,
            toSnapshot: SnapshotDal,
            lastTransactionId?: string
        ): CreateTransactionReq => {
            if (experimentInstance.isOpen('dm_fragments')) {
                return createFragmentTransactions(fromSnapshot, toSnapshot, lastTransactionId)
            }

            const {siteAPI} = extensionAPI as DocumentServicesModelExtApi
            const siteVersion = `${siteAPI.getSiteVersion()}`
            const {result: diff, error} = converter.convertData(
                fromSnapshot,
                toSnapshot,
                logger,
                experimentInstance,
                extensionAPI
            )
            if (error) {
                return {error}
            }
            if (_.isEmpty(diff)) {
                return {
                    payload: {
                        transactions: []
                    }
                }
            }
            const transaction: PendingTransaction = {
                correlationId: toSnapshot.id,
                actions: diff! as Action[],
                metadata: {
                    correlationId: toSnapshot.id,
                    lastTransactionId,
                    siteVersion,
                    initiator: {initiatorType: InitiatorType.DM_CLIENT}
                },
                envSessionId: siteAPI.getEnvSessionId()
            }

            const payload: SaveRequest = {
                branchId: siteAPI.getBranchId(),
                ...(experimentInstance.isOpen('dm_moveCsaveToNewProto')
                    ? {atomicTransactions: [{transaction}]}
                    : {transactions: [transaction]})
            }

            return {payload}
        }

        return {
            continuousSavePayloadBuilder: {
                createTransactionsCSave
            }
        }
    }

    return {
        name: 'continuousSavePayloadBuilder',
        createExtensionAPI
    }
}

export interface ContinuousSavePayloadBuilderExtensionAPI extends ExtensionAPI {
    continuousSavePayloadBuilder: {
        createTransactionsCSave(
            fromSnapshot: Null<SnapshotDal>,
            toSnapshot: SnapshotDal,
            lastTransactionId?: string
        ): CreateTransactionReq
    }
}

export {createExtension}
